import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Package, 
  CheckCircle, 
  X, 
  Loader2,
  AlertCircle,
  Store,
  Calendar,
  User,
  Check,
  GitMerge,
  Barcode,
  Camera,
  Upload,
  Image as ImageIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PendingProductsReview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Master-Bild Upload
  const masterImageInputRef = useRef(null);
  const [masterImageUploading, setMasterImageUploading] = useState(false);
  const [masterImagePreview, setMasterImagePreview] = useState(null);
  const [masterImagePath, setMasterImagePath] = useState(null);

  // Duplicate confirmation dialog
  const [dupConfirmOpen, setDupConfirmOpen] = useState(false);
  const [dupConfirmData, setDupConfirmData] = useState(null);
  const [mergeLoading, setMergeLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== 'superadmin') {
      navigate('/');
      return;
    }
    fetchPendingProducts();
  }, [user, navigate]);

  const fetchPendingProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/superadmin/pending-products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch pending products:', error);
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  // 🔗 EAN-MERGE: Abgeschnittene EAN durch korrekte Master-EAN ersetzen
  const handleMergeEAN = async (shortEan, correctEan) => {
    if (!dupConfirmData?.product) return;
    setMergeLoading(true);
    try {
      const res = await axios.post(`${API}/superadmin/merge-ean`, {
        product_id: dupConfirmData.product.id,
        short_ean: shortEan,
        correct_ean: correctEan,
        customer_id: dupConfirmData.product.customer_id
      });
      toast.success(
        `EAN korrigiert: ${shortEan} → ${correctEan} · ${res.data.mappings_updated} Einträge aktualisiert.`,
        { duration: 5000 }
      );
      setDupConfirmOpen(false);
      setDupConfirmData(null);
      fetchPendingProducts();
    } catch (err) {
      toast.error('Fehler beim EAN-Merge: ' + (err.response?.data?.detail || err.message));
    } finally {
      setMergeLoading(false);
    }
  };

  const handleApprove = async (product, editedName = null, force = false) => {
    try {
      const response = await axios.post(`${API}/superadmin/approve-product`, {
        mapping_id: product.id,
        product_name: editedName || product.product_name,
        force
      });
      // New 2-phase flow: requires_confirmation = show dialog BEFORE approving
      if (response.data?.requires_confirmation) {
        setDupConfirmData({
          product,
          editedName,
          pendingProduct: response.data.pending_product,
          possibleDuplicates: response.data.possible_duplicates || []
        });
        setDupConfirmOpen(true);
        return;
      }
      // Legacy fallback: old backend returned possible_duplicate AFTER approving
      if (response.data?.possible_duplicate) {
        toast.warning(`Freigegeben. Hinweis: Ähnliches Produkt: "${response.data.possible_duplicate.product_name}"`);
        setEditDialogOpen(false);
        fetchPendingProducts();
        return;
      }
      toast.success('Produkt freigegeben. Kein Duplikat gefunden.');
      setEditDialogOpen(false);
      setProducts(prev => prev.filter(p => p.id !== product.id));
      fetchPendingProducts();
    } catch (error) {
      console.error('Failed to approve product:', error);
      const status = error.response?.status;
      if (status === 401) {
        toast.error('Sitzung abgelaufen – bitte Seite neu laden und erneut anmelden.', { duration: 6000 });
      } else if (status === 404) {
        toast.error('Produkt nicht gefunden oder bereits bearbeitet.');
        fetchPendingProducts();
      } else {
        toast.error('Fehler beim Freigeben: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (product) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/superadmin/reject-product`, {
        mapping_id: product.id,
        reason: rejectReason
      });
      toast.success('Produkt abgelehnt');
      setRejectDialogOpen(false);
      setRejectReason('');
      setProducts(prev => prev.filter(p => p.id !== product.id));
      fetchPendingProducts();
    } catch (error) {
      console.error('Failed to reject product:', error);
      toast.error('Fehler beim Ablehnen');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setEditedName(product.product_name);
    setMasterImagePreview(product.local_image_data || product.image_url || null);
    setMasterImagePath(product.master_image_path || null);
    setEditDialogOpen(true);
  };

  // Master-Bild Upload Handler
  const handleMasterImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedProduct) return;

    // Vorschau sofort anzeigen
    const reader = new FileReader();
    reader.onloadend = () => setMasterImagePreview(reader.result);
    reader.readAsDataURL(file);

    setMasterImageUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ean', selectedProduct.gtin);
      const resp = await axios.post(`${API}/superadmin/master-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMasterImagePath(resp.data.path);
      toast.success('Master-Bild erfolgreich hochgeladen');
    } catch (err) {
      toast.error('Upload fehlgeschlagen: ' + (err.response?.data?.detail || err.message));
      setMasterImagePreview(selectedProduct.local_image_data || selectedProduct.image_url || null);
    } finally {
      setMasterImageUploading(false);
      if (masterImageInputRef.current) masterImageInputRef.current.value = '';
    }
  };

  const openRejectDialog = (product) => {
    setSelectedProduct(product);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Produkt-Freigaben
            </h1>
            <p className="text-muted-foreground text-sm">
              {products.length} Produkte warten auf Freigabe
            </p>
          </div>
        </div>

        {/* Summary Card */}
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="py-3 sm:py-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600 flex-shrink-0" />
              <div>
                <p className="font-semibold text-blue-900 text-sm sm:text-base">Neue Produkte zur Prüfung</p>
                <p className="text-xs sm:text-sm text-blue-700">
                  Mitarbeiter haben {products.length} neue{products.length === 1 ? 's' : ''} Produkt{products.length !== 1 ? 'e' : ''} angelegt
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Product List */}
        {products.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <h3 className="font-semibold mb-1">Alles erledigt!</h3>
              <p className="text-muted-foreground text-sm">
                Keine neuen Produkte zur Freigabe
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {products.map(product => (
              <Card key={product.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-3 sm:p-4">

                  {/* === MOBILE: Stack-Layout / DESKTOP: Row-Layout === */}
                  <div className="flex flex-col gap-3">

                    {/* Row 1: Bild + Name + GTIN */}
                    <div className="flex items-start gap-3">
                      {/* Bild – kleiner auf Mobile */}
                      <div className="flex-shrink-0">
                        {(product.local_image_data || product.image_url) ? (
                          <img
                            src={product.local_image_data || product.image_url}
                            alt={product.product_name}
                            className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg border"
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        ) : (
                          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="h-5 w-5 sm:h-6 sm:w-6 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Produktname + GTIN */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm sm:text-base leading-snug line-clamp-2">
                          {product.product_name}
                        </h3>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          GTIN: <span className="font-mono">{product.gtin}</span>
                        </p>
                      </div>
                    </div>

                    {/* Row 2: Metadaten (Standort, User, Datum) */}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground px-0.5">
                      <div className="flex items-center gap-1">
                        <Store className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate max-w-[130px]">{product.location_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate max-w-[120px]">{product.created_by_name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 flex-shrink-0" />
                        {formatDate(product.created_at)}
                      </div>
                    </div>

                    {/* Row 3: Aktions-Buttons – volle Breite auf Mobile */}
                    <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
                      <Button
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700 h-9 text-xs sm:text-sm"
                        onClick={() => handleApprove(product)}
                        disabled={submitting}
                      >
                        <CheckCircle className="h-3.5 w-3.5 mr-1 flex-shrink-0" />
                        Freigeben
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 h-9 text-xs sm:text-sm"
                        onClick={() => openEditDialog(product)}
                        disabled={submitting}
                      >
                        <span className="sm:hidden">Bearbeiten</span>
                        <span className="hidden sm:inline">Bearbeiten &amp; Freigeben</span>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-9 w-9 p-0 text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0"
                        onClick={() => openRejectDialog(product)}
                        disabled={submitting}
                        title="Ablehnen"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit & Approve Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle>Produktname bearbeiten</DialogTitle>
          </DialogHeader>

          {selectedProduct && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-muted-foreground mb-1">GTIN</p>
                <p className="font-mono font-semibold">{selectedProduct.gtin}</p>
              </div>

              <div className="space-y-1">
                <Label>Produktname</Label>
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="Produktname eingeben"
                />
              </div>

              {/* Master-Bild Upload */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <ImageIcon className="h-4 w-4" />
                  Master-Bild (für alle Kunden sichtbar)
                </Label>
                <input
                  type="file"
                  accept="image/*"
                  ref={masterImageInputRef}
                  onChange={handleMasterImageUpload}
                  className="hidden"
                />
                {masterImagePreview ? (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                    <img
                      src={masterImagePreview}
                      alt="Master Bild Vorschau"
                      className="w-16 h-16 object-cover rounded-lg border"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="flex-1 space-y-1">
                      {masterImagePath ? (
                        <p className="text-xs text-green-600 font-medium">✓ In Cloud gespeichert</p>
                      ) : (
                        <p className="text-xs text-blue-600 font-medium">Vorschau (noch nicht hochgeladen)</p>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => masterImageInputRef.current?.click()}
                        disabled={masterImageUploading}
                      >
                        {masterImageUploading ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Camera className="h-3 w-3 mr-1" />
                        )}
                        {masterImageUploading ? 'Lädt hoch...' : 'Anderes Bild'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-10 border-dashed"
                    onClick={() => masterImageInputRef.current?.click()}
                    disabled={masterImageUploading}
                    data-testid="master-image-upload-btn"
                  >
                    {masterImageUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    {masterImageUploading ? 'Lädt hoch...' : 'Master-Bild hochladen'}
                  </Button>
                )}
                <p className="text-xs text-muted-foreground">
                  Dieses Bild wird für alle Kunden als Standard angezeigt (höchste Priorität).
                </p>
              </div>

              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Das Produkt wird mit diesem Namen in die Master-Datenbank aufgenommen
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => handleApprove(selectedProduct, editedName)}
              disabled={submitting || !editedName.trim()}
              className="bg-green-600 hover:bg-green-700"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Freigeben
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <X className="h-5 w-5 text-red-600" />
              Produkt ablehnen
            </DialogTitle>
          </DialogHeader>

          {selectedProduct && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="font-semibold">{selectedProduct.product_name}</p>
                <p className="text-sm text-muted-foreground">GTIN: {selectedProduct.gtin}</p>
              </div>

              <div className="space-y-1">
                <Label>Grund (optional)</Label>
                <Input
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="z.B. Duplikat, falscher Name..."
                />
              </div>

              <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200">
                <p className="text-xs text-orange-700 dark:text-orange-300">
                  Das Produkt bleibt für den lokalen Standort verfügbar, wird aber nicht in die Master-Datenbank übernommen
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              variant="destructive"
              onClick={() => handleReject(selectedProduct)}
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Ablehnen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Confirmation Dialog */}
      <Dialog open={dupConfirmOpen} onOpenChange={(open) => { if (!open) { setDupConfirmOpen(false); setDupConfirmData(null); } }}>
        <DialogContent className="sm:max-w-[580px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5" />
              Mögliches Duplikat gefunden
            </DialogTitle>
            <DialogDescription>
              Im Master-Katalog wurden ähnliche Produkte gefunden. Bitte prüfe, ob es sich um ein Duplikat handelt.
            </DialogDescription>
          </DialogHeader>

          {dupConfirmData && (
            <div className="space-y-4 py-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Neues Produkt (zur Freigabe)</p>
                <div className="flex items-center gap-3 p-3 rounded-lg border-2 border-green-200 bg-green-50 dark:bg-green-900/20">
                  {dupConfirmData.pendingProduct?.image ? (
                    <img src={dupConfirmData.pendingProduct.image} alt="" className="w-12 h-12 object-cover rounded border flex-shrink-0" onError={(e) => e.target.style.display='none'} />
                  ) : (
                    <div className="w-12 h-12 bg-muted rounded flex items-center justify-center flex-shrink-0">
                      <Package className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-sm">{dupConfirmData.pendingProduct?.product_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">EAN: {dupConfirmData.pendingProduct?.gtin}</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                  Bereits im Master-Katalog ({dupConfirmData.possibleDuplicates.length})
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {dupConfirmData.possibleDuplicates.map((dup, idx) => {
                    const isEanPrefixMatch = dup.ean_prefix_match === true;
                    const shortEan = dupConfirmData.pendingProduct?.gtin;
                    return (
                      <div key={idx} className={`flex items-center gap-3 p-3 rounded-lg border ${isEanPrefixMatch ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20' : 'border-amber-200 bg-amber-50 dark:bg-amber-900/20'}`}>
                        {dup.image_url ? (
                          <img src={dup.image_url} alt="" className="w-12 h-12 object-cover rounded border flex-shrink-0" onError={(e) => e.target.style.display='none'} />
                        ) : (
                          <div className="w-12 h-12 bg-muted rounded flex items-center justify-center flex-shrink-0">
                            <Package className="h-5 w-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-semibold text-sm">{dup.product_name}</p>
                            {isEanPrefixMatch && (
                              <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded font-medium">
                                <Barcode className="h-3 w-3" /> Abgeschnittener Barcode
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">
                            EAN: {dup.ean}
                            {isEanPrefixMatch && (
                              <span className="ml-1 text-blue-600">← beginnt mit „{shortEan}"</span>
                            )}
                          </p>
                        </div>
                        {isEanPrefixMatch && (
                          <Button
                            size="sm"
                            className="flex-shrink-0 bg-blue-600 hover:bg-blue-700 text-white gap-1 text-xs"
                            onClick={() => handleMergeEAN(shortEan, dup.ean)}
                            disabled={mergeLoading}
                            data-testid={`merge-ean-btn-${idx}`}
                          >
                            {mergeLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <GitMerge className="h-3 w-3" />}
                            EAN übernehmen
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded">
                {dupConfirmData.possibleDuplicates.some(d => d.ean_prefix_match)
                  ? '🔵 Blau markierte Einträge wurden durch einen abgeschnittenen Barcode-Scan erkannt. Klicke auf "EAN übernehmen" um den Kunden-Eintrag automatisch auf die vollständige EAN zu korrigieren.'
                  : 'Handelt es sich um dasselbe Produkt, lehne den Vorschlag ab. Ist es ein anderes Produkt, klicke auf "Trotzdem freigeben".'}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setDupConfirmOpen(false); setDupConfirmData(null); }}>
              Abbrechen
            </Button>
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => {
                if (dupConfirmData) openRejectDialog(dupConfirmData.product);
                setDupConfirmOpen(false);
                setDupConfirmData(null);
              }}
            >
              <X className="h-4 w-4 mr-1" /> Als Duplikat ablehnen
            </Button>
            <Button
              className="bg-green-600 hover:bg-green-700 text-white"
              onClick={() => {
                if (dupConfirmData) handleApprove(dupConfirmData.product, dupConfirmData.editedName, true);
                setDupConfirmOpen(false);
                setDupConfirmData(null);
              }}
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              <Check className="h-4 w-4 mr-1" /> Trotzdem freigeben
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};
